import { format, isSameMonth, isSameYear } from "date-fns";

export const getCountWidth = (count) => {
  const digits = String(count || 2).length; // fallback to 2 digits
  return `${Math.min(Math.max(digits * 8, 16), 40)}px`;
};

export function formatHandle(value) {
  // Normalize null/undefined/empty
  if (!value || typeof value !== "string" || value.trim() === "") {
    return null; // return null so UI can show "-"
  }

  // Remove any existing @ from DB value
  const clean = value.startsWith("@") ? value.slice(1) : value;

  return clean; // always return clean string without "@"
}

export const generateTimeOptions = (intervalMinutes = 15, format = "24h") => {
  const options = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      const hh = hour.toString().padStart(2, "0");
      const mm = minute.toString().padStart(2, "0");

      const label = format === "12h" ? formatTo12Hour(hh, mm) : `${hh}:${mm}`;

      options.push({
        label,
        value: `${hh}:${mm}`
      });
    }
  }

  return options;
};

const formatTo12Hour = (hh, mm) => {
  const hour = Number(hh);
  const suffix = hour >= 12 ? "PM" : "AM";
  const formattedHour = hour % 12 || 12;
  return `${formattedHour.toString().padStart(2, "0")}:${mm} ${suffix}`;
};

//  ROUND DOWN (2:48 → 2:45)
export const getCurrentRoundedTime = (interval = 15) => {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = Math.floor(minutes / interval) * interval;

  now.setMinutes(roundedMinutes, 0, 0);

  const hh = now.getHours().toString().padStart(2, "0");
  const mm = now.getMinutes().toString().padStart(2, "0");

  return `${hh}:${mm}`;
};

// -----------------------------
// DATE UTILS
// -----------------------------
export const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

export const formatCustomRange = (range) => {
  const [start, end] = range;
  if (!start || !end) return "";

  const sameMonth = isSameMonth(start, end);
  const sameYear = isSameYear(start, end);

  if (sameMonth && sameYear) {
    // "1 - 7 Jul '24"
    return `${format(start, "d")} - ${format(end, "d MMM ''yy")}`;
  }

  if (sameYear) {
    // "1 Jun - 7 Jul '24"
    return `${format(start, "d MMM")} - ${format(end, "d MMM ''yy")}`;
  }

  // "28 Dec '23 - 3 Jan '24"
  return `${format(start, "d MMM ''yy")} - ${format(end, "d MMM ''yy")}`;
};

export const getFileName = (fileOrUrl) => {
  if (!fileOrUrl) return "";
  if (fileOrUrl instanceof File) return fileOrUrl.name; // uploaded file
  if (typeof fileOrUrl === "string") {
    try {
      const url = new URL(fileOrUrl);
      return url.pathname.split("/").pop(); // get last part of URL path
    } catch {
      // If it's not a full URL, fallback
      return fileOrUrl.split("/").pop();
    }
  }
  return "";
};

export const LINKEDIN_REGEX =
  /^(https?:\/\/)?(?:[\w-]+\.)?linkedin\.com\/in\/[A-Za-z0-9_-]+\/?(\?.*)?$/i;

export const debounce = (fn, delay = 500) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

export const reorderDropdown = (options = [], selectedValue) => {
  if (!selectedValue) return options;

  // Find the selected option
  const selected = options.find((o) => o.value === selectedValue);
  if (!selected) return options;

  // Filter out selected from rest
  const rest = options.filter((o) => o.value !== selectedValue);

  // Return selected first, then rest
  return [selected, ...rest];
};

export const DEFAULT_PROFILE_IMG =
  "https://live-hashfame.sgp1.digitaloceanspaces.com/creator_profiles/default_profile.png";

// date format: YYYY-MM-DD
const formatDate = (date) => {
  return date.toISOString().split("T")[0]; // YYYY-MM-DD
};

// get date range for last N days
export const getLastNDaysRange = (days) => {
  const toDate = new Date(); // today
  const fromDate = new Date();
  fromDate.setDate(toDate.getDate() - days);

  return {
    fromDate: formatDate(fromDate),
    toDate: formatDate(toDate)
  };
};

// validateEmail
export const validateEmail = (value) => {
  if (!value) return "";
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value) ? "" : "Please enter a valid email address";
};

// validatePhone
export const validatePhone = (value) => {
  if (!value) return "";

  const phoneRegex = /^[0-9]{10}$/; // exactly 10 digits
  return phoneRegex.test(value)
    ? ""
    : "Please enter a valid 10 digit phone number";
};

export const normalizeStatus = (status) => {
  if (!status) return null;
  return status.toLowerCase().replace("-", "_");
};

export const formatTimeZone = (value) => {
  if (!value) return "-";
  try {
    return format(new Date(value), "MMM dd, yyyy h:mm a");
  } catch {
    return "-";
  }
};
